import { ALL_OPTION } from '@/common/constants';
import { Address, Option } from '@/common/types';
import { PaymentMethodType } from '@/modules/employee/employee.types';
import { LockboxType } from '@/modules/employer/components/Lockbox/lockbox.types';
import theme from '@/styles/theme';

export enum CheckStatus {
  PENDING = 'PENDING',
  ISSUED = 'ISSUED',
  CLEARED = 'CLEARED',
  VOIDED = 'VOIDED',
  EXPIRED = 'EXPIRED',
  EXPIRING = 'EXPIRING',
  REISSUING = 'REISSUING',
  REISSUE_REQUIRED = 'REISSUE_REQUIRED',
  VOIDING = 'VOIDING',
  READY = 'READY',
  SENT = 'SENT',
  REJECTED = 'REJECTED',
  REISSUED = 'REISSUED',
  RETURNED = 'RETURNED',
}

export enum CheckType {
  CLAIM = 'CLAIM',
  LOCKBOX = 'LOCKBOX',
}

export const sectionStatusBackgroundColor = {
  REJECTED: theme.colors.dangerContainer,
  VOIDED: theme.colors.dangerContainer,
  EXPIRED: theme.colors.dangerContainer,
  REISSUED: theme.colors.dangerContainer,
  VOIDING: theme.colors.module,
  EXPIRING: theme.colors.module,
  REISSUING: theme.colors.module,
  PENDING: theme.colors.module,
  ISSUED: theme.colors.module,
  REISSUE_REQUIRED: theme.colors.module,
  SENT: theme.colors.successContainer,
  CLEARED: theme.colors.successContainer,
  READY: theme.colors.successContainer,
  RETURNED: theme.colors.dangerContainer,
};
export const sectionStatusBorderColor = {
  REJECTED: theme.colors.dangerBorder,
  VOIDED: theme.colors.dangerBorder,
  EXPIRED: theme.colors.dangerBorder,
  REISSUED: theme.colors.dangerBorder,
  VOIDING: theme.colors.border2,
  EXPIRING: theme.colors.border2,
  REISSUING: theme.colors.border2,
  PENDING: theme.colors.border2,
  REISSUE_REQUIRED: theme.colors.border2,
  SENT: theme.colors.successBorder,
  ISSUED: theme.colors.border2,
  CLEARED: theme.colors.successBorder,
  READY: theme.colors.successBorder,
  RETURNED: theme.colors.dangerBorder,
};

export const checkStatusColor = {
  PENDING: theme.colors.checkStatusPending,
  ISSUED: theme.colors.checkStatusIssued,
  CLEARED: theme.colors.checkStatusCleared,
  VOIDED: theme.colors.checkStatusVoided,
  EXPIRED: theme.colors.checkStatusExpired,
  REISSUE_REQUIRED: theme.colors.checkStatusReissued,
  REISSUED: theme.colors.checkStatusReissued,
  VOIDING: theme.colors.checkStatusVoiding,
  EXPIRING: theme.colors.checkStatusExpiring,
  REISSUING: theme.colors.checkStatusReissuing,
  READY: theme.colors.checkStatusReady,
  SENT: theme.colors.checkStatusReady,
  REJECTED: theme.colors.checkStatusRejected,
  RETURNED: theme.colors.checkStatusVoided,
};

export enum VoidReason {
  NOT_RECEIVED = 'NOT_RECEIVED',
  WRONG_ADDRESS = 'WRONG_ADDRESS',
  LOST_CHECK = 'LOST_CHECK',
  NO_LONGER_NEEDED = 'NO_LONGER_NEEDED',
  RETURNED_CHECK = 'RETURNED_CHECK',
}

export interface CheckPayload {
  amount?: number;
  check_id?: number;
  claim_ids?: number[];
  lockbox_id?: number;
  check_type?: CheckType;
  current_status?: CheckStatus;
  employee_id?: number;
  employee_name?: string;
  employer_name?: string;
  issued_date?: string;
  pay_to?: string;
  status_date?: string;
  check_address?: Address;
  void_reason?: VoidReason;
  payment_method_id?: number;
  reissue_check_id?: number;
  reissue_payment_method_type?: PaymentMethodType;
  lockbox_type?: LockboxType;
}

export interface Check {
  amount?: number;
  id?: string;
  claimIds?: string[];
  lockboxId?: string;
  type?: CheckType;
  status?: CheckStatus;
  employeeId?: string;
  employeeName?: string;
  employerName?: string;
  issuedDate?: string;
  payTo?: string;
  statusDate?: string;
  address?: Address;
  voidReason?: VoidReason;
  paymentMethodId?: string;
  reissueCheckId?: string;
  reissuePaymentMethodType?: PaymentMethodType;
  lockboxType?: LockboxType;
}

export const CHECK_STATUSES_OPTIONS: Option[] = [
  ALL_OPTION,
  {
    key: CheckStatus.PENDING,
    value: CheckStatus.PENDING,
    title: 'Pending',
  },
  {
    key: CheckStatus.ISSUED,
    value: CheckStatus.ISSUED,
    title: 'Issued',
  },
  {
    key: CheckStatus.CLEARED,
    value: CheckStatus.CLEARED,
    title: 'Cleared',
  },
  {
    key: CheckStatus.VOIDED,
    value: CheckStatus.VOIDED,
    title: 'Voided',
  },
  {
    key: CheckStatus.EXPIRED,
    value: CheckStatus.EXPIRED,
    title: 'Expired',
  },
  {
    key: CheckStatus.EXPIRING,
    value: CheckStatus.EXPIRING,
    title: 'Expiring',
  },
  {
    key: CheckStatus.REISSUING,
    value: CheckStatus.REISSUING,
    title: 'Reissuing',
  },
  {
    key: CheckStatus.REISSUE_REQUIRED,
    value: CheckStatus.REISSUE_REQUIRED,
    title: 'Reissue required',
  },
  {
    key: CheckStatus.VOIDING,
    value: CheckStatus.VOIDING,
    title: 'Voiding',
  },
  {
    key: CheckStatus.RETURNED,
    value: CheckStatus.RETURNED,
    title: 'Returned',
  },
];

export const VOID_REASON_OPTIONS: Option[] = [
  {
    key: VoidReason.NOT_RECEIVED,
    value: VoidReason.NOT_RECEIVED,
    title: 'Not received',
  },
  {
    key: VoidReason.WRONG_ADDRESS,
    value: VoidReason.WRONG_ADDRESS,
    title: 'Wrong address',
  },
  {
    key: VoidReason.LOST_CHECK,
    value: VoidReason.LOST_CHECK,
    title: 'Lost check',
  },
  {
    key: VoidReason.NO_LONGER_NEEDED,
    value: VoidReason.NO_LONGER_NEEDED,
    title: 'No longer needed',
  },
  {
    key: VoidReason.RETURNED_CHECK,
    value: VoidReason.RETURNED_CHECK,
    title: 'Returned check',
  },
];
